/**
 * Central dungeon progression config (M7-D2).
 *
 * System supports Dungeon Level 1–100 and Item Level 1–100.
 * Mutation (Standard / Hard / Brutal) is independent of dungeon level.
 *
 * Player growth: classBaseStats.ts (approved G1 / A3).
 * Enemy level scaling: approved E1 segmented curve (below).
 * Equipment class-level requirements: equipmentProgression.ts.
 *
 * Future socketing/enchanting must plug into combat stats — do not treat
 * item level as the sole source of character power.
 */

import type { LootRarity } from './lootTables';

export const DUNGEON_MUTATIONS = ['standard', 'hard', 'brutal'] as const;
export type DungeonMutationId = (typeof DUNGEON_MUTATIONS)[number];

export function isDungeonMutationId(value: string): value is DungeonMutationId {
  return (DUNGEON_MUTATIONS as readonly string[]).includes(value);
}

/** Supported dungeon level range (architecture + playable). */
export const DUNGEON_LEVEL_SYSTEM_RANGE = {
  min: 1,
  max: 100,
} as const;

/**
 * Playable / DEV UI dungeon level range.
 * Unlock sequencing is separate and not enforced here.
 */
export const DUNGEON_LEVEL_INITIAL_PLAYABLE_RANGE = {
  min: 1,
  max: 100,
} as const;

/**
 * Player-facing Gate runs use this level until zones/story assign
 * stages per area/biome (and a difficulty). Players never select level.
 * Debug/Settings keep an explicit level control for testing.
 */
export const PLAYER_DEFAULT_DUNGEON_LEVEL = 1;

/** Supported item-level range (architecture + generation). */
export const ITEM_LEVEL_SYSTEM_RANGE = {
  min: 1,
  max: 100,
} as const;

/**
 * Effective item-level cap for generation / scaling.
 * Was 18 for the early 1–15 gate; now matches system max 100.
 * Raising later must not invalidate stored equipment.
 */
export const ITEM_LEVEL_INITIAL_CAP = ITEM_LEVEL_SYSTEM_RANGE.max;

/** @deprecated alias — prefer ITEM_LEVEL_SYSTEM_RANGE.max / ITEM_LEVEL_INITIAL_CAP */
export const ITEM_LEVEL_CAP = ITEM_LEVEL_INITIAL_CAP;

/** Pass Infinity in sims for uncapped reference. */
export function clampItemLevel(
  value: number,
  cap: number = ITEM_LEVEL_INITIAL_CAP,
): number {
  const n = Math.max(ITEM_LEVEL_SYSTEM_RANGE.min, Math.floor(value));
  if (!Number.isFinite(cap)) return n;
  return Math.min(Math.floor(cap), n);
}

export function clampDungeonLevel(level: number): number {
  const n = Math.floor(level);
  if (!Number.isFinite(n) || n < DUNGEON_LEVEL_SYSTEM_RANGE.min) {
    return DUNGEON_LEVEL_SYSTEM_RANGE.min;
  }
  return Math.min(DUNGEON_LEVEL_SYSTEM_RANGE.max, n);
}

export function clampPlayableDungeonLevel(level: number): number {
  const n = clampDungeonLevel(level);
  return Math.min(DUNGEON_LEVEL_INITIAL_PLAYABLE_RANGE.max, n);
}

/**
 * Approved E1 segmented enemy level scaling.
 * DL 1–60: +10% per level (from L1 baseline).
 * DL 61–100: +5% per additional level, continuing from the L60 multiplier.
 */
export const ENEMY_LEVEL_SEGMENT_BREAK = 60;
/** Early-segment step (DL 1–60). */
export const ENEMY_LEVEL_STAT_STEP = 0.1;
/** Late-segment step (DL 61–100). */
export const ENEMY_LEVEL_STAT_STEP_LATE = 0.05;

export interface MutationEnemyMult {
  hp: number;
  damage: number;
  guard: number;
}

/** Approved M1 mutation enemy multipliers (after level scaling). */
export const MUTATION_ENEMY_MULT: Record<DungeonMutationId, MutationEnemyMult> =
  {
    standard: { hp: 1, damage: 1, guard: 1 },
    hard: { hp: 1.25, damage: 1.2, guard: 1.15 },
    brutal: { hp: 1.5, damage: 1.4, guard: 1.25 },
  };

/**
 * Multiplier for enemy stats from dungeon level (mutation applied separately).
 * Continuous at the segment break:
 *   mult(60) = 1 + 59×0.10
 *   mult(61) = mult(60) + 0.05
 */
export function enemyLevelMult(dungeonLevel: number): number {
  const L = clampDungeonLevel(dungeonLevel);
  const earlySteps = Math.min(L, ENEMY_LEVEL_SEGMENT_BREAK) - 1;
  let mult = 1 + earlySteps * ENEMY_LEVEL_STAT_STEP;
  if (L > ENEMY_LEVEL_SEGMENT_BREAK) {
    mult += (L - ENEMY_LEVEL_SEGMENT_BREAK) * ENEMY_LEVEL_STAT_STEP_LATE;
  }
  return mult;
}

export function scaleEnemyStat(
  base: number,
  dungeonLevel: number,
  mutation: DungeonMutationId,
  kind: keyof MutationEnemyMult,
): number {
  const levelMult = enemyLevelMult(dungeonLevel);
  const mut = MUTATION_ENEMY_MULT[mutation][kind];
  return Math.max(0, Math.floor(base * levelMult * mut));
}

export interface DungeonProgressionConfig {
  dungeonLevel: number;
  mutation: DungeonMutationId;
}

/** Legacy difficulty → independent level + mutation (never remap brutal → high Standard). */
export function migrateLegacyDifficulty(
  difficulty: string | undefined | null,
): DungeonProgressionConfig {
  if (difficulty === 'hard') {
    return { dungeonLevel: 1, mutation: 'hard' };
  }
  if (difficulty === 'brutal') {
    return { dungeonLevel: 1, mutation: 'brutal' };
  }
  return { dungeonLevel: 1, mutation: 'standard' };
}

export function resolveDungeonProgression(args: {
  dungeonLevel?: number;
  mutation?: string;
  /** @deprecated legacy field */
  difficulty?: string;
}): DungeonProgressionConfig {
  if (
    typeof args.dungeonLevel === 'number' ||
    (args.mutation != null && isDungeonMutationId(args.mutation))
  ) {
    const mutation: DungeonMutationId = isDungeonMutationId(args.mutation ?? '')
      ? (args.mutation as DungeonMutationId)
      : 'standard';
    return {
      dungeonLevel: clampDungeonLevel(args.dungeonLevel ?? 1),
      mutation,
    };
  }
  return migrateLegacyDifficulty(args.difficulty);
}

/** Item-level band from dungeon level + mutation shift.
 * Near IL cap 100 the band may compress (e.g. DL100 → 99–100).
 * Accepted M7-D2 design characteristic — rarity/affix/mutation still vary.
 */
export function itemLevelBand(
  dungeonLevel: number,
  mutation: DungeonMutationId,
  cap: number = ITEM_LEVEL_INITIAL_CAP,
): { min: number; max: number } {
  const L = clampDungeonLevel(dungeonLevel);
  const shift = mutation === 'brutal' ? 2 : mutation === 'hard' ? 1 : 0;
  const rawMin = Math.max(1, L - 1 + shift);
  const rawMax = Math.max(rawMin, L + 2 + shift);
  return {
    min: clampItemLevel(rawMin, cap),
    max: clampItemLevel(rawMax, cap),
  };
}

export function baselineRarityWeights(
  dungeonLevel: number,
): Array<{ rarity: LootRarity; weight: number }> {
  const L = clampDungeonLevel(dungeonLevel);
  if (L <= 3) {
    return [
      { rarity: 'common', weight: 55 },
      { rarity: 'uncommon', weight: 28 },
      { rarity: 'rare', weight: 12 },
      { rarity: 'epic', weight: 4 },
      { rarity: 'legendary', weight: 1 },
    ];
  }
  if (L <= 7) {
    return [
      { rarity: 'common', weight: 40 },
      { rarity: 'uncommon', weight: 30 },
      { rarity: 'rare', weight: 18 },
      { rarity: 'epic', weight: 9 },
      { rarity: 'legendary', weight: 3 },
    ];
  }
  if (L <= 12) {
    return [
      { rarity: 'common', weight: 28 },
      { rarity: 'uncommon', weight: 30 },
      { rarity: 'rare', weight: 24 },
      { rarity: 'epic', weight: 13 },
      { rarity: 'legendary', weight: 5 },
    ];
  }
  if (L <= 25) {
    return [
      { rarity: 'common', weight: 18 },
      { rarity: 'uncommon', weight: 28 },
      { rarity: 'rare', weight: 28 },
      { rarity: 'epic', weight: 18 },
      { rarity: 'legendary', weight: 8 },
    ];
  }
  if (L <= 50) {
    return [
      { rarity: 'common', weight: 12 },
      { rarity: 'uncommon', weight: 24 },
      { rarity: 'rare', weight: 30 },
      { rarity: 'epic', weight: 22 },
      { rarity: 'legendary', weight: 12 },
    ];
  }
  if (L <= 75) {
    return [
      { rarity: 'common', weight: 8 },
      { rarity: 'uncommon', weight: 20 },
      { rarity: 'rare', weight: 30 },
      { rarity: 'epic', weight: 26 },
      { rarity: 'legendary', weight: 16 },
    ];
  }
  // DL 76–100 — still varied; does not guarantee legendary every drop
  return [
    { rarity: 'common', weight: 5 },
    { rarity: 'uncommon', weight: 15 },
    { rarity: 'rare', weight: 28 },
    { rarity: 'epic', weight: 30 },
    { rarity: 'legendary', weight: 22 },
  ];
}

export function rarityWeightsForProgression(
  dungeonLevel: number,
  mutation: DungeonMutationId,
): Array<{ rarity: LootRarity; weight: number }> {
  const weights = baselineRarityWeights(dungeonLevel).map((w) => ({ ...w }));
  const bump =
    mutation === 'brutal' ? 0.35 : mutation === 'hard' ? 0.18 : 0;
  if (bump === 0) return weights;
  const common = weights.find((w) => w.rarity === 'common');
  if (!common) return weights;
  const take = common.weight * bump;
  common.weight -= take;
  const higher = weights.filter((w) => w.rarity !== 'common');
  const higherTotal = higher.reduce((s, w) => s + w.weight, 0) || 1;
  for (const w of higher) {
    w.weight += take * (w.weight / higherTotal);
  }
  return weights;
}

export const EQUIPMENT_DROP_CHANCE_BY_MUTATION: Record<
  DungeonMutationId,
  { elite: number; bossKill: number }
> = {
  standard: { elite: 0.35, bossKill: 0.55 },
  hard: { elite: 0.4, bossKill: 0.55 },
  brutal: { elite: 0.45, bossKill: 0.6 },
};

export const BOSS_CHEST_COUNT_BY_MUTATION: Record<DungeonMutationId, number> = {
  standard: 2,
  hard: 3,
  brutal: 4,
};

/** Confirmed affix counts. */
export const AFFIX_COUNT_BY_RARITY_V2: Record<LootRarity, number> = {
  common: 0,
  uncommon: 1,
  rare: 2,
  epic: 3,
  legendary: 4,
};

/** APPROVED_INITIAL rarity base-stat multipliers (loot bands). */
export const RARITY_BASE_STAT_MULT: Record<LootRarity, number> = {
  common: 1,
  uncommon: 1.04,
  rare: 1.08,
  epic: 1.12,
  legendary: 1.16,
};

/** APPROVED_INITIAL affix magnitude multipliers (applied at roll time). */
export const AFFIX_MAGNITUDE_MULT: Record<LootRarity, number> = {
  common: 1,
  uncommon: 1,
  rare: 1.1,
  epic: 1.2,
  legendary: 1.3,
};

/** APPROVED_INITIAL item-level step for equipment base stats. */
export const ITEM_LEVEL_STAT_STEP = 0.04;

/**
 * Effective equipment base (weapon damage or inherent armour) before combat floors.
 * Fractional until existing combat calculation boundary.
 */
export function scaleEquipmentBaseStat(
  definitionBase: number,
  rarity: LootRarity | string | undefined,
  itemLevel: number | undefined,
): number {
  const r = (rarity ?? 'common') as LootRarity;
  const mult = RARITY_BASE_STAT_MULT[r] ?? 1;
  const ilvl = Math.max(1, Math.floor(itemLevel ?? 1));
  return (
    Math.max(0, definitionBase) * mult * (1 + (ilvl - 1) * ITEM_LEVEL_STAT_STEP)
  );
}

export function scaleAffixMagnitude(
  rawValue: number,
  rarity: LootRarity,
  valueMin: number,
  valueMax: number,
): number {
  const scaled = Math.floor(rawValue * (AFFIX_MAGNITUDE_MULT[rarity] ?? 1));
  // Allow slight expansion above max from rarity mult; never below min.
  const expandedMax = Math.max(
    valueMax,
    Math.floor(valueMax * (AFFIX_MAGNITUDE_MULT[rarity] ?? 1)),
  );
  return Math.max(valueMin, Math.min(expandedMax, scaled));
}

/** Qualitative UI copy — no unapproved raw multipliers. */
export function describeProgressionChallenge(
  config: DungeonProgressionConfig,
): { challenge: string; rewards: string } {
  const L = config.dungeonLevel;
  const mut =
    config.mutation === 'brutal'
      ? 'Brutal'
      : config.mutation === 'hard'
        ? 'Hard'
        : 'Standard';
  const band =
    L <= 3
      ? 'early'
      : L <= 15
        ? 'mid'
        : L <= 40
          ? 'late'
          : L <= 75
            ? 'deep'
            : 'endgame';
  return {
    challenge: `Level ${L} · ${mut} · ${band} threat`,
    rewards:
      config.mutation === 'standard'
        ? 'Baseline loot for this level'
        : `${mut} improves rarity odds and chest size at this level`,
  };
}
